import {
  Button,
  Divider,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  GeneralStarOutlineIcon,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import Image from 'next/image';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import mediaUrl from '@/assets/raf/referee-landing-media.jpg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import { useRouter } from '@/hooks/useRouter';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { applyRafCode } from '@/utility/raf';

import { useReferralCodeEligibility } from './data';
import { Share } from './Share';

type TRefereeLandingProps = {
  code: string;
  referee: string;
};

export const RefereeLanding = ({ code, referee }: TRefereeLandingProps) => {
  const intl = useIntl();
  const router = useRouter();

  const isAuthenticated = useSelector(selectAuthenticated);

  const { data: referralCodeEligibilityData } = useReferralCodeEligibility(code);

  const handleStartExploring = () => router.push(`/rv-search`, undefined, { shallow: true });

  useEffect(() => {
    // Redirect to search page if the referral code is not eligible
    if (referralCodeEligibilityData && !referralCodeEligibilityData.eligible) {
      router.push(`/rv-search`, undefined, { shallow: true });
    }

    // Store the code if eligible so we can auto apply in checkout
    if (referralCodeEligibilityData && referralCodeEligibilityData.eligible) {
      applyRafCode(code);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCodeEligibilityData]);

  return (
    <MaxWidth className="content-center px-6 py-10 m-auto lg:p-0 grid lg:grid-cols-2 min-h-[60vh] bg-canvas-100 lg:bg-white">
      <div className="overflow-hidden lg:p-10 lg:flex lg:flex-col lg:justify-center">
        <div className="flex flex-col text-center gap-4 lg:gap-6 lg:text-left">
          <div className="flex self-center justify-center w-16 h-16 rounded-full bg-canvas-200 lg:hidden">
            <GeneralStarOutlineIcon className="self-center" width={32} height={32} />
          </div>

          <Heading variant={EHeadingStyleVariant.H4}>
            <FormattedMessage
              defaultMessage="You just received $50 off from {referee}!"
              id="/A+/PU"
              values={{ referee }}
            />
          </Heading>

          <Text variant={ETextStyleVariant.MediumRegular}>
            <FormattedMessage
              defaultMessage="Congratulations! You've received a referral credit to use on your next trip. The discount will auto-apply at checkout; if not, find the code at the end of your referral link and enter it manually."
              id="4vW890"
            />
          </Text>

          <div>
            <Button
              className="w-full md:w-auto"
              variant={EButtonColorVariant.Primary}
              label={intl.formatMessage({ defaultMessage: 'Start exploring', id: 'faZtRk' })}
              disabled={!referralCodeEligibilityData?.eligible}
              onClick={handleStartExploring}
            />
          </div>
        </div>

        {isAuthenticated && (
          <>
            <Divider className="my-6" />

            <div className="flex flex-col gap-2">
              <Text variant={ETextStyleVariant.MediumRegular}>
                <FormattedMessage
                  defaultMessage="Want more discounts? Share your referral link with friends, earn more rewards and #liveoutdoorsy."
                  id="wcq9nM"
                />
              </Text>

              <Share />
            </div>
          </>
        )}
      </div>

      <div className="hidden overflow-hidden lg:flex lg:flex-col lg:justify-center">
        <div className="relative -mr-10 h-[450px]">
          <Image
            alt="Outdoorsy referral"
            src={mediaUrl}
            role="presentation"
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            loading="lazy"
          />
        </div>
      </div>
    </MaxWidth>
  );
};
